import { useEffect, useMemo, useState } from "react";
import { useTranslation, Trans } from "@i18n";
import { Box, AutocompleteRenderInputParams } from "@mui/material";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";
import { issueTypeIcons } from "./useJiraTickets";
import {
  jiraIdMapper,
  issueTypeIdToLabel,
  initialStatusIdByIssueType,
  getStatusesForIssueType,
  impactDataSource,
  severityDataSource,
  severityDataSourceCustomSla,
  getJiraStatusHex,
  statusCategoryMapper,
} from "@Helpers";

export type ChangeTypePayload = {
  ticketId: string;
  newIssueType: string;
  newStatus: string;
  impact?: string;
  severity?: string;
};

type LabeledId = { id: string; label: string };

/** One option of the impact / severity pickers. `severityDataSource` (standard SLA model) carries no
 *  `description`; `impactDataSource` and `severityDataSourceCustomSla` do — and the severity picker
 *  below switches between the two, so the description is optional here. */
type PickerOption = { id: string; label: string; description?: string };

type Ticket = {
  _id: string;
  fields?: {
    issueType?: { id?: string; label?: string };
    issueStatus?: { id?: string; label?: string };
    issueStatusCategory?: { id?: string; label?: string };
    impact?: { id?: string; label?: string } | Record<string, never>;
    severity?: { id?: string; label?: string } | Record<string, never>;
  };
};

interface Props {
  isVisible: boolean;
  ticket: Ticket | null;
  onCancel: () => void;
  onConfirm: (payload: ChangeTypePayload) => Promise<void> | void;
  loading?: boolean;
  initialTargetTypeId?: string;
  customSupportSlaEnabled?: boolean;
}

const ChangeTicketTypeModal = ({
  isVisible,
  ticket,
  onCancel,
  onConfirm,
  loading,
  initialTargetTypeId,
  customSupportSlaEnabled = false,
}: Props) => {
  const { t } = useTranslation();

  const currentTypeId = ticket?.fields?.issueType?.id ?? "";
  const currentStatusId = ticket?.fields?.issueStatus?.id ?? "";
  const currentStatusLabel = ticket?.fields?.issueStatus?.label ?? "";

  const targetTypeOptions: LabeledId[] = useMemo(() => {
    const labelMap = issueTypeIdToLabel(t);
    return Object.keys(labelMap)
      .filter((id) => id !== currentTypeId)
      .map((id) => ({ id, label: labelMap[id] ?? id }));
  }, [currentTypeId, t]);

  const [targetTypeId, setTargetTypeId] = useState<string>("");
  const [newStatusId, setNewStatusId] = useState<string>("");
  const [impactId, setImpactId] = useState<string>("");
  const [severityId, setSeverityId] = useState<string>("");

  // Read previously-stored impact/severity from the ticket so we can pre-fill the
  // bug fields when the user changes type back to Report a Bug (handles
  // bug → question → bug round-trips).
  const existingImpactId =
    ticket?.fields?.impact && "id" in ticket.fields.impact ? (ticket.fields.impact.id ?? "") : "";
  const existingSeverityId =
    ticket?.fields?.severity && "id" in ticket.fields.severity ? (ticket.fields.severity.id ?? "") : "";

  useEffect(() => {
    if (!isVisible) return;
    const labelMap = issueTypeIdToLabel();
    const fallbackType = Object.keys(labelMap).find((id) => id !== currentTypeId) ?? "";
    const defaultType =
      initialTargetTypeId && initialTargetTypeId !== currentTypeId ? initialTargetTypeId : fallbackType;
    setTargetTypeId(defaultType);
    setNewStatusId(defaultType ? (initialStatusIdByIssueType[defaultType] ?? "") : "");
    // Pre-fill from the ticket's preserved fields (the backend keeps these even
    // when the type was previously a non-bug).
    setImpactId(defaultType === jiraIdMapper["Report a Bug"] ? existingImpactId : "");
    setSeverityId(defaultType === jiraIdMapper["Report a Bug"] ? existingSeverityId : "");
  }, [isVisible, ticket?._id, currentTypeId, existingImpactId, existingSeverityId, initialTargetTypeId]);

  useEffect(() => {
    if (!targetTypeId) return;
    setNewStatusId(initialStatusIdByIssueType[targetTypeId] ?? "");
    if (targetTypeId === jiraIdMapper["Report a Bug"]) {
      // Re-populate from the ticket's preserved bug fields each time the user
      // switches the target back to Bug.
      setImpactId(existingImpactId);
      setSeverityId(existingSeverityId);
    } else {
      setImpactId("");
      setSeverityId("");
    }
  }, [targetTypeId, existingImpactId, existingSeverityId]);

  const newStatusOptions: LabeledId[] = useMemo(() => {
    if (!targetTypeId) return [];
    return getStatusesForIssueType(targetTypeId, t);
  }, [targetTypeId, t]);

  const selectedTargetTypeOption = useMemo(
    () => targetTypeOptions.find((o) => o.id === targetTypeId) ?? null,
    [targetTypeOptions, targetTypeId],
  );
  const selectedNewStatusOption = useMemo(
    () => newStatusOptions.find((o) => o.id === newStatusId) ?? null,
    [newStatusOptions, newStatusId],
  );
  const isBugTarget = targetTypeId === jiraIdMapper["Report a Bug"];

  const canConfirm =
    !!ticket &&
    !!targetTypeId &&
    !!newStatusId &&
    (!isBugTarget || (customSupportSlaEnabled ? !!severityId : !!impactId && !!severityId));

  const handleConfirm = async () => {
    if (!ticket || !canConfirm) return;
    const payload: ChangeTypePayload = {
      ticketId: ticket._id,
      newIssueType: targetTypeId,
      newStatus: newStatusId,
    };
    if (isBugTarget) {
      payload.severity = severityId;
      if (!customSupportSlaEnabled) payload.impact = impactId;
    }
    await onConfirm(payload);
  };

  const renderTypeOption = (props: React.HTMLAttributes<HTMLLIElement>, option: LabeledId) => (
    <li {...props} key={option.id} className="flex items-center gap-2 p-3">
      <IconComponent name={issueTypeIcons[option.id] ?? ""} width="1.25rem" height="1.25rem" />
      <span>{option.label}</span>
    </li>
  );

  const renderStatusOption = (props: React.HTMLAttributes<HTMLLIElement>, option: LabeledId) => {
    const hex = getJiraStatusHex(option.id, statusCategoryMapper(t)[option.id]?.id);
    return (
      <li {...props} key={option.id} className="px-3 py-2">
        <Box sx={{ backgroundColor: hex }} className="font-semibold px-3 py-1 text-white rounded text-sm w-fit">
          {option.label}
        </Box>
      </li>
    );
  };

  const statusInputSx = (statusId: string) => {
    const hex = getJiraStatusHex(statusId, statusCategoryMapper(t)[statusId]?.id);
    if (!hex) return { width: "100%" };
    return {
      width: "100%",
      "& .MuiInputBase-root, & .MuiOutlinedInput-root": {
        background: `${hex} !important`,
        overflow: "hidden",
      },
      "& .MuiOutlinedInput-root .MuiOutlinedInput-input, & .MuiInputBase-input": {
        backgroundColor: `${hex} !important`,
        color: "#fff !important",
        WebkitTextFillColor: "#fff !important",
      },
      "& .MuiOutlinedInput-input::placeholder, & .MuiInputBase-input::placeholder": {
        color: "#fff !important",
        opacity: 0.85,
      },
      "& .MuiSvgIcon-root": {
        color: "#fff",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: `${hex} !important`,
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: `${hex} !important`,
      },
    };
  };

  const renderHeader = () => <Box className="flex items-center font-semibold">{t("Change Type")}</Box>;

  const renderBody = () => (
    <Box className="flex flex-col w-full gap-4 items-start">
      {isBugTarget && (
        <Box className="flex items-center gap-4 p-4 rounded-lg bg-bg_warning border border-warning w-full">
          <IconComponent name="info" width="1.375rem" height="1.375rem" />
          <Text className="text-sm">
            <Trans i18nKey="bugSLAWarning" components={{ bold: <strong /> }} />
          </Text>
        </Box>
      )}

      <Box className="flex flex-col w-full">
        <Text className="font-semibold">{t("Change Type To")}</Text>
        <AutoComplete
          options={targetTypeOptions}
          value={selectedTargetTypeOption}
          valueField="id"
          getOptionLabel={(option) => option.label || ""}
          onChange={(_e: React.SyntheticEvent, v: LabeledId | null) => setTargetTypeId(v?.id ?? "")}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextFieldComponent
              {...params}
              placeholder={t("Select Type")}
              InputProps={{
                ...params.InputProps,
                startAdornment: targetTypeId ? (
                  <Box className="flex ps-2">
                    <IconComponent name={issueTypeIcons[targetTypeId] ?? ""} width="1.25rem" height="1.25rem" />
                  </Box>
                ) : null,
              }}
            />
          )}
          renderOption={renderTypeOption}
          sx={{ width: "100%" }}
        />
      </Box>

      <Box className="flex gap-4 w-full items-end">
        <Box className="flex flex-col gap-2 flex-1 min-w-0">
          <Text className="font-semibold">{t("Current Status")}</Text>
          {(() => {
            const hex = getJiraStatusHex(currentStatusId, statusCategoryMapper(t)[currentStatusId]?.id);
            return (
              <Box
                sx={{
                  boxSizing: "border-box",
                  backgroundColor: hex || "#f5f5f5",
                  height: "2.125rem",
                  borderRadius: "0.25rem",
                  border: `1px solid ${hex || "#666A84"}`,
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  fontStyle: "normal",
                  lineHeight: "normal",
                  color: "#fff",
                  padding: "0 0.625rem",
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
              >
                {currentStatusLabel || "-"}
              </Box>
            );
          })()}
        </Box>
        <Box className="flex pb-3 shrink-0">
          <IconComponent name="arrowRight" width="1rem" height="1rem" />
        </Box>
        <Box className="flex flex-col gap-2 flex-1 min-w-0">
          <Text className="font-semibold">{t("New Status")}</Text>
          <AutoComplete
            key={`new-status-${targetTypeId}`}
            options={newStatusOptions}
            value={selectedNewStatusOption}
            valueField="id"
            getOptionLabel={(option) => option.label || ""}
            minWidth={0}
            onChange={(_e: React.SyntheticEvent, v: LabeledId | null) => setNewStatusId(v?.id ?? "")}
            renderInput={(params: AutocompleteRenderInputParams) => (
              <TextFieldComponent {...params} placeholder={t("Select Status")} />
            )}
            renderOption={renderStatusOption}
            sx={statusInputSx(newStatusId)}
          />
        </Box>
      </Box>

      {isBugTarget && (
        <>
          {!customSupportSlaEnabled && (
            <Box className="flex flex-col gap-2 w-full">
              <Text className="font-semibold">{t("Impact")}</Text>
              <AutoComplete
                options={impactDataSource(t)}
                value={impactId || null}
                valueField="id"
                getOptionLabel={(option: PickerOption) => option.label || ""}
                onChange={(_e: React.SyntheticEvent, v: PickerOption | null) => setImpactId(v?.id ?? "")}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <TextFieldComponent {...params} placeholder={t("Select Impact")} />
                )}
                renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: PickerOption) => (
                  <li {...props} key={option.id} className="flex flex-col items-start p-4 gap-1">
                    <span className="text-sm font-semibold">{option.label}</span>
                    <span className="text-sm text-text_light">{option.description}</span>
                  </li>
                )}
                sx={{ width: "100%" }}
              />
            </Box>
          )}

          <Box className="flex flex-col gap-2 w-full">
            <Text className="font-semibold">{t("Severity")}</Text>
            <AutoComplete
              options={customSupportSlaEnabled ? severityDataSourceCustomSla(t) : severityDataSource(t)}
              value={severityId || null}
              valueField="id"
              getOptionLabel={(option: PickerOption) => option.label || ""}
              onChange={(_e: React.SyntheticEvent, v: PickerOption | null) => setSeverityId(v?.id ?? "")}
              renderInput={(params: AutocompleteRenderInputParams) => (
                <TextFieldComponent {...params} placeholder={t("Select Severity")} />
              )}
              renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: PickerOption) =>
                customSupportSlaEnabled ? (
                  <li {...props} key={option.id} className="flex flex-col items-start p-4 gap-2">
                    <span className="text-sm font-semibold text-primary_text">{option.label}</span>
                    <span className="text-sm text-primary_text">{option.description}</span>
                  </li>
                ) : (
                  <li {...props} key={option.id} className="px-4 py-2">
                    {option.label}
                  </li>
                )
              }
              sx={{ width: "100%" }}
            />
          </Box>
        </>
      )}
    </Box>
  );

  const renderFooter = () => (
    <div className="flex gap-4">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        loading={loading}
        disabled={!canConfirm}
        onClick={handleConfirm}
      >
        <Text>{t("Confirm")}</Text>
      </ButtonComponent>

      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} disabled={loading} onClick={onCancel}>
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  return (
    <ModalStateDialogComponent
      minWidth="32rem"
      isVisible={isVisible}
      onCancel={onCancel}
      headerTitle={renderHeader()}
      modelContent={renderBody()}
      footerContent={renderFooter()}
      footerBorderTop
    />
  );
};

export default ChangeTicketTypeModal;
